import { HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { AuthService } from '@core/auth/auth.service';
import { QuoteType } from '@shared/enums/quote-type.enum';
import { SalesRepAssignmentStatus, SalesRepSubjectType, SalesRepSubjectTypeDisplay } from '@shared/enums/sales-rep-assignment.enum';
import { RejectSalesRepDialogData, SalesRepAssignmentListView, SalesRepAssignmentStatusCounts, SalesRepSubjectFields } from '@shared/models/domain/custom-views.model';
import { SalesRepAssignmentView } from '@shared/models/domain/domain-view.model';
import { SalesRepAssignment } from '@shared/models/domain/domain.model';
import { PageResponse } from '@shared/models/response/page-response.model';
import { AlertService } from '@shared/services/alert.service';
import { ReadOnlyCoreApiService } from '@shared/services/common/read-only-core-api.service.';
import { Observable, catchError, map, of, take, tap } from 'rxjs';


@Injectable({
    providedIn: 'root',
})
export class SalesRepAssignmentService extends ReadOnlyCoreApiService<SalesRepAssignment> {
    constructor() {
        super('admin/sales-rep-assignments');
    }

    approve(id: string): Observable<SalesRepAssignment> {
        return this.httpService.post<PageResponse<SalesRepAssignment>>(`${this.baseUrl}${this.serviceUrl}/${id}/approve`, {})
            .pipe(map((res) => res.item));
    }

    reject(id: string, note: string | null): Observable<SalesRepAssignment> {
        return this.httpService.post<PageResponse<SalesRepAssignment>>(`${this.baseUrl}${this.serviceUrl}/${id}/reject`, { note: note?.trim() || null })
            .pipe(map((res) => res.item));
    }

    approveMany(ids: string[]): Observable<boolean> {
        return this.httpService.post<PageResponse<boolean>>(`${this.baseUrl}${this.serviceUrl}/approve`, { ids })
            .pipe(map((res) => res.item));
    }
}

@Injectable({
    providedIn: 'root',
})
export class SalesRepAssignmentViewService extends ReadOnlyCoreApiService<SalesRepAssignmentListView> {
    private authService = inject(AuthService);
    private alert = inject(AlertService);

    private readonly backgroundHeaders = new HttpHeaders({ Intercept: 'false' });
    private readonly pendingCountState = signal(0);

    readonly pendingCount = this.pendingCountState.asReadonly();

    constructor() {
        super('admin/view/sales-rep-assignments');
    }

    getStatusCounts(): Observable<SalesRepAssignmentStatusCounts> {
        return this.httpService.get<PageResponse<SalesRepAssignmentStatusCounts>>(`${this.baseUrl}${this.serviceUrl}/status-counts`)
            .pipe(map((res) => res.item));
    }

    loadPendingCount(): void {
        if (!this.authService.canApproveSalesRepChanges) {
            this.pendingCountState.set(0);
            return;
        }
        this.httpService.get<PageResponse<number>>(`${this.baseUrl}${this.serviceUrl}/pending-count`, { headers: this.backgroundHeaders })
            .pipe(
                map((res) => res.item ?? 0),
                catchError(() => of(this.pendingCountState())),
                take(1),
            )
            .subscribe((count) => this.pendingCountState.set(count));
    }

    getPendingForSubject(subjectType: SalesRepSubjectType, subjectId: string | number): Observable<SalesRepAssignmentView | null> {
        return this.httpService.get<PageResponse<SalesRepAssignmentView>>(`${this.baseUrl}${this.serviceUrl}/pending`, {
            headers: this.backgroundHeaders,
            params: { subjectType, subjectId },
        }).pipe(
            map((res) => res?.item ?? null),
            catchError(() => of(null)),
        );
    }

    checkSentForApproval(subjectType: SalesRepSubjectType, subjectId: string | number, requestedSalesRepUserId: string | null): Observable<SalesRepAssignmentView | null> {
        return this.getPendingForSubject(subjectType, subjectId).pipe(
            tap((pending) => {
                if (!this.isSentForApproval(pending, requestedSalesRepUserId)) return;
                this.alert.successToast('Sent for approval', 'The current sales rep stays until the change is approved.', 4000);
                this.loadPendingCount();
            }),
        );
    }

    isSentForApproval(pending: SalesRepAssignmentView | null, requestedSalesRepUserId: string | null): boolean {
        return pending?.status === SalesRepAssignmentStatus.Pending
            && (pending.requestedSalesRepUserId || null) === (requestedSalesRepUserId || null);
    }

    pendingApprovalHint(pending: SalesRepAssignmentView | null): string | null {
        return pending ? `Pending approval: ${pending.requestedSalesRepName?.trim() || 'None'}` : null;
    }

    subjectLabel(view: SalesRepSubjectFields): string {
        return view.subjectQuoteType === QuoteType.Invoice ? 'Invoice' : SalesRepSubjectTypeDisplay[view.subjectType]?.label ?? '';
    }

    rejectDialogData(view: SalesRepSubjectFields & Pick<SalesRepAssignmentView, 'customerFullName' | 'requestedByName' | 'requestedSalesRepName' | 'currentSalesRepName'>): RejectSalesRepDialogData {
        const customer = view.customerFullName?.trim() || 'this customer';
        const subjectNumber = `${this.subjectLabel(view)} #${view.subjectNumber || view.subjectId}`;
        const isCustomer = view.subjectType === SalesRepSubjectType.UserProfile;
        return {
            requesterName: view.requestedByName?.trim() || 'Someone',
            requestedRepName: view.requestedSalesRepName?.trim() || null,
            subject: isCustomer ? `the customer profile for ${customer}` : `${subjectNumber.charAt(0).toLowerCase()}${subjectNumber.slice(1)} for ${customer}`,
            owner: isCustomer ? customer : subjectNumber,
            currentRepName: view.currentSalesRepName?.trim() || null,
        };
    }
}
